import React, { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import BottomNav from './BottomNav';
import { clockGate } from './clockGate';
import HourlyHeaderMobile from './HourlyHeaderMobile';
import CommissionedHeaderMobile from './CommissionedHeaderMobile';
import OfficeCleanerHeaderMobile from './OfficeCleanerHeaderMobile';
import { UserCircle } from '@phosphor-icons/react';

function formatElapsedCompact(seconds) {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function formatTimeShort(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

export default function Header({ user, onLogout, currentTab, onTabChange, onAccount, hidden, showMelville }) {
  // Detailers (commissioned / hourly) get the Melville queue when the bridge
  // is configured. Office cleaners don't detail cars, so they don't.
  const mel = showMelville ? ['Detail Queue'] : [];
  const tabsByType = {
    admin:          ['Dashboard', 'Jobs', 'Attendance', 'Reports', 'Statements', 'Settings'],
    commissioned:   [...mel, 'Submit Job', 'My Jobs', 'Buffing', 'Insurance', 'Invoice', 'Statement'],
    hourly:         [...mel, 'Submit Job', 'My Jobs', 'Buffing', 'Insurance', 'Invoice', 'Statement'],
    office_cleaner: ['My Day', 'My Records', 'Insurance', 'Invoice', 'Statement'],
  };
  const tabs = tabsByType[user.type] || ['My Jobs'];
  const isAdmin = user.type === 'admin';

  const canClock = user.type === 'hourly' || user.type === 'office_cleaner';
  const [clockEvent, setClockEvent] = useState(null);
  const [elapsed, setElapsed] = useState(0);
  const [clockLoading, setClockLoading] = useState(false);
  const [clockLoaded, setClockLoaded] = useState(false);       // status fetch finished
  const [reminderDismissed, setReminderDismissed] = useState(false); // "Later" this session
  const timerRef = useRef(null);

  useEffect(() => {
    if (canClock) loadClockStatus();
  }, []);

  useEffect(() => {
    clearInterval(timerRef.current);
    if (clockEvent?.clock_in_at && !clockEvent?.clock_out_at) {
      const update = () => setElapsed(Math.floor((Date.now() - new Date(clockEvent.clock_in_at)) / 1000));
      update();
      timerRef.current = setInterval(update, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [clockEvent]);

  async function loadClockStatus() {
    try { setClockEvent(await api.clockStatus()); } catch { /* ignore */ }
    finally { setClockLoaded(true); }
  }

  // GPS is REQUIRED for clock-in / clock-out. If permission is denied or the
  // device can't fix a location, refuse — otherwise the worker could clock in
  // from home and the admin would have no way to verify.
  async function getRequiredCoords() {
    if (!navigator.geolocation) {
      throw new Error('This device does not support location. Cannot clock in/out.');
    }
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        pos => resolve({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        err => {
          const msg = err.code === err.PERMISSION_DENIED
            ? 'Location permission is blocked. Enable location for this site in your phone settings, then try again.'
            : err.code === err.POSITION_UNAVAILABLE
            ? 'Cannot find your location. Make sure GPS is on and you have signal, then try again.'
            : err.code === err.TIMEOUT
            ? 'Location took too long. Move outside or near a window and try again.'
            : 'Could not get your location. Please try again.';
          reject(new Error(msg));
        },
        { timeout: 15000, enableHighAccuracy: true, maximumAge: 0 },
      );
    });
  }

  async function handleClockIn() {
    setClockLoading(true);
    try {
      const coords = await getRequiredCoords();
      await api.clockIn(coords);
      await loadClockStatus();
    } catch (err) { alert(err.message); }
    finally { setClockLoading(false); }
  }

  async function handleClockOut() {
    if (!window.confirm('Are you sure you want to Clock Out?')) return;
    setClockLoading(true);
    try {
      const coords = await getRequiredCoords();
      await api.clockOut(coords);
      await loadClockStatus();
    } catch (err) { alert(err.message); }
    finally { setClockLoading(false); }
  }

  async function handleClockReset() {
    if (!window.confirm("Reset clock? This will erase today's clock in/out so you can start again.")) return;
    setClockLoading(true);
    try {
      await api.clockReset();
      setClockEvent(null);
      setElapsed(0);
    } catch (err) { alert(err.message); }
    finally { setClockLoading(false); }
  }

  const isClockedIn  = clockEvent?.clock_in_at && !clockEvent?.clock_out_at;
  const isClockedOut = clockEvent?.clock_out_at;

  // Who sees the overlay and whether they can get past it — see clockGate.js.
  const gate = clockGate({
    userType:    user.type,
    clockLoaded,
    clockedIn:   !!isClockedIn,
    clockedOut:  !!isClockedOut,
    currentTab,
    dismissed:   reminderDismissed,
  });
  const showReminder = gate.show;
  const lockedOut    = gate.locked;
  // The home-screen badge follows the same "hasn't started the day" state.
  const needsClockIn = clockLoaded && canClock && !isClockedIn && !isClockedOut;

  // Red dot on the (installed) app icon while the worker hasn't clocked in.
  // The badge persists on the home-screen icon even after the app is closed,
  // and clears the moment they clock in/out. No-op in browsers without the
  // Badging API (e.g. iOS Safari) or when the app isn't installed.
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('setAppBadge' in navigator)) return;
    if (needsClockIn) navigator.setAppBadge(1).catch(() => {});
    else navigator.clearAppBadge && navigator.clearAppBadge().catch(() => {});
  }, [needsClockIn]);

  if (hidden) return null;

  // ── DESKTOP HEADER (admin + large screens) ──────────────────────────────
  return (
    <>
      {/* ── Desktop header (≥769px) — full layout, always visible ── */}
      <header style={{ background: '#ffffff', position: 'sticky', top: 0, zIndex: 100, borderBottom: '1px solid #e5e7eb' }}
        className="desktop-header">
        <div style={{ maxWidth: 1100, margin: '0 auto', padding: '0 16px' }}>
          {/* Top bar */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 60 }}>
            <img src={process.env.PUBLIC_URL + '/ACTS_CAR_DETAILING.png'} alt="Acts Car Detailing"
              style={{ height: 44, objectFit: 'contain' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 13, color: '#374151' }}>
                {user.name}
                <span style={{
                  marginLeft: 8, fontSize: 11, background: '#f3f4f6', color: '#4A7A91',
                  padding: '2px 8px', borderRadius: 0, fontWeight: 700, textTransform: 'uppercase',
                  border: '1px solid #4A7A91',
                }}>
                  {user.type === 'office_cleaner' ? 'office cleaner' : user.type}
                </span>
              </span>
              <button className="btn btn-ghost btn-sm" onClick={onAccount}
                style={{ color: '#374151', borderColor: '#d1d5db', fontSize: 12 }}>
                My Account
              </button>
              <button className="btn btn-ghost btn-sm" onClick={onLogout}
                style={{ color: '#374151', borderColor: '#d1d5db' }}>
                Logout
              </button>
            </div>
          </div>

          {/* Clock bar — hourly & office_cleaner (desktop) */}
          {canClock && (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 16, padding: '8px 0', borderTop: '1px solid #e5e7eb' }}>
              {!clockEvent && !isClockedIn && !isClockedOut && (
                <>
                  <button onClick={handleClockIn} disabled={clockLoading}
                    style={{ background: '#059669', color: '#fff', border: 'none', borderRadius: 0, padding: '7px 20px', fontSize: 14, fontWeight: 700, cursor: clockLoading ? 'default' : 'pointer', opacity: clockLoading ? 0.6 : 1 }}>
                    {clockLoading ? 'GPS...' : '▶ Clock In'}
                  </button>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Not clocked in</span>
                </>
              )}
              {isClockedIn && (
                <>
                  <div style={{ fontFamily: 'monospace', fontSize: 28, fontWeight: 700, color: '#34D399', letterSpacing: 2 }}>
                    {formatElapsedCompact(elapsed)}
                  </div>
                  <span style={{ color: '#6b7280', fontSize: 12 }}>in {formatTimeShort(clockEvent.clock_in_at)}</span>
                  <button onClick={handleClockOut} disabled={clockLoading}
                    style={{ background: '#DC2626', color: '#fff', border: 'none', borderRadius: 0, padding: '6px 16px', fontSize: 13, fontWeight: 700, cursor: clockLoading ? 'default' : 'pointer', opacity: clockLoading ? 0.6 : 1 }}>
                    {clockLoading ? 'GPS...' : '■ Clock Out'}
                  </button>
                  <button onClick={handleClockReset} disabled={clockLoading}
                    style={{ background: 'transparent', color: '#6b7280', border: '1px solid #d1d5db', borderRadius: 0, padding: '5px 12px', fontSize: 11, cursor: clockLoading ? 'default' : 'pointer' }}>
                    Undo
                  </button>
                </>
              )}
              {isClockedOut && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>In: <strong style={{ color: '#1a1a1a' }}>{formatTimeShort(clockEvent.clock_in_at)}</strong></span>
                  <span style={{ color: '#6b7280', fontSize: 13 }}>Out: <strong style={{ color: '#1a1a1a' }}>{formatTimeShort(clockEvent.clock_out_at)}</strong></span>
                  <span style={{ fontFamily: 'monospace', fontSize: 18, fontWeight: 700, color: '#34D399' }}>
                    {clockEvent.hours_worked ? clockEvent.hours_worked.toFixed(2) : '—'}h
                  </span>
                  <button onClick={handleClockReset} disabled={clockLoading}
                    style={{ background: 'transparent', color: '#DC2626', border: '1px solid #DC2626', borderRadius: 0, padding: '5px 14px', fontSize: 12, fontWeight: 600, cursor: clockLoading ? 'default' : 'pointer' }}>
                    Reset
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab bar */}
          <div style={{ display: 'flex', borderTop: '1px solid #e5e7eb' }}>
            {tabs.map(t => (
              <button key={t} onClick={() => onTabChange(t)} style={{
                padding: '11px 18px', border: 'none', background: 'transparent', fontSize: 14,
                fontWeight: 600, color: currentTab === t ? '#4A7A91' : '#9ca3af',
                borderBottom: currentTab === t ? '2px solid #4A7A91' : '2px solid transparent',
                cursor: 'pointer', transition: 'all 0.2s',
              }}>{t}</button>
            ))}
          </div>
        </div>
      </header>

      {/* ── Mobile header (≤768px) ── */}
      <>
        {/* Hourly workers get the redesigned shift-dial header */}
        {user.type === 'hourly' && (
          <HourlyHeaderMobile
            user={user}
            clockEvent={clockEvent}
            elapsed={elapsed}
            isClockedIn={isClockedIn}
            isClockedOut={isClockedOut}
            clockLoading={clockLoading}
            onClockIn={handleClockIn}
            onClockOut={handleClockOut}
            onLogout={onLogout}
            onAccount={onAccount}
          />
        )}

        {/* Commissioned workers get the redesigned earnings-dial header */}
        {user.type === 'commissioned' && (
          <CommissionedHeaderMobile user={user} onLogout={onLogout} onAccount={onAccount} />
        )}

        {/* Office cleaners get the redesigned night-shift-dial header */}
        {user.type === 'office_cleaner' && (
          <OfficeCleanerHeaderMobile
            user={user}
            clockEvent={clockEvent}
            elapsed={elapsed}
            isClockedIn={isClockedIn}
            isClockedOut={isClockedOut}
            clockLoading={clockLoading}
            onClockIn={handleClockIn}
            onClockOut={handleClockOut}
            onLogout={onLogout}
            onAccount={onAccount}
          />
        )}

        {/* Compact sticky top bar — any other user type (fallback) */}
        {user.type !== 'hourly' && user.type !== 'commissioned' && user.type !== 'office_cleaner' && (
        <header style={{ background: '#ffffff', position: 'sticky', top: 0, zIndex: 100, borderBottom: '1px solid #e5e7eb' }}
          className="mobile-header">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: 52, padding: '0 16px' }}>
            <img src={process.env.PUBLIC_URL + '/ACTS_CAR_DETAILING.png'} alt="Acts"
              style={{ height: 34, objectFit: 'contain' }} />

            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              {/* Clock timer inline when clocked in */}
              {canClock && isClockedIn && (
                <div style={{ fontFamily: 'monospace', fontSize: 16, fontWeight: 700, color: '#059669', letterSpacing: 1 }}>
                  {formatElapsedCompact(elapsed)}
                </div>
              )}
              {canClock && isClockedOut && (
                <span style={{ fontSize: 13, fontWeight: 600, color: '#34D399' }}>
                  {clockEvent.hours_worked?.toFixed(2)}h ✓
                </span>
              )}

              <button onClick={onAccount} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                padding: '4px', lineHeight: 1, display: 'flex', alignItems: 'center',
              }} title="My Account">
                <UserCircle size={26} weight="duotone" color="#1e3a4a" />
              </button>
              <button onClick={onLogout} style={{
                background: '#f3f4f6', border: 'none', cursor: 'pointer',
                fontSize: 12, fontWeight: 600, color: '#374151',
                padding: '6px 12px', borderRadius: 0,
              }}>
                Logout
              </button>
            </div>
          </div>
        </header>
        )}

        {/* Mobile clock bar — legacy strip; hourly + office cleaner now carry
            the clock inside their dial headers, so this only remains as a
            fallback for any other clockable type. */}
        {canClock && user.type !== 'hourly' && user.type !== 'office_cleaner' && (
          <div className="mobile-clock-bar" style={{ position: 'sticky', top: 52, zIndex: 99 }}>
            {!clockEvent && !isClockedIn && !isClockedOut && (
              <div style={{
                background: '#FFD000',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
              }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 800, color: '#111', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                    ⏱ Not clocked in
                  </div>
                  <div style={{ fontSize: 11, color: '#555' }}>Tap to start your day</div>
                </div>
                <button
                  onClick={handleClockIn}
                  disabled={clockLoading}
                  style={{
                    background: '#111', color: '#fff', border: 'none', borderRadius: 0,
                    padding: '10px 20px', fontSize: 14, fontWeight: 800,
                    cursor: clockLoading ? 'default' : 'pointer', opacity: clockLoading ? 0.6 : 1,
                    letterSpacing: 0.5,
                  }}
                >
                  {clockLoading ? 'GPS…' : '▶ CLOCK IN'}
                </button>
              </div>
            )}

            {isClockedIn && (
              <div style={{
                background: '#1e3a4a',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.25)',
              }}>
                <div>
                  <div style={{
                    fontFamily: 'monospace', fontSize: 28, fontWeight: 700,
                    color: '#FFD000', letterSpacing: 2, lineHeight: 1,
                  }}>
                    {formatElapsedCompact(elapsed)}
                  </div>
                  <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 3 }}>
                    In at {formatTimeShort(clockEvent.clock_in_at)}
                    {clockEvent.address_in && <span> · {clockEvent.address_in}</span>}
                  </div>
                </div>
                <button
                  onClick={handleClockOut}
                  disabled={clockLoading}
                  style={{
                    background: '#dc2626', color: '#fff', border: 'none', borderRadius: 0,
                    padding: '10px 20px', fontSize: 14, fontWeight: 800,
                    cursor: clockLoading ? 'default' : 'pointer', opacity: clockLoading ? 0.6 : 1,
                    letterSpacing: 0.5,
                  }}
                >
                  {clockLoading ? 'GPS…' : '■ CLOCK OUT'}
                </button>
              </div>
            )}

            {isClockedOut && (
              <div style={{
                background: '#15803d',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '10px 16px',
                boxShadow: '0 2px 6px rgba(0,0,0,0.2)',
              }}>
                <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                  <div>
                    <div style={{ fontSize: 10, color: '#bbf7d0', textTransform: 'uppercase', letterSpacing: 0.5 }}>In</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{formatTimeShort(clockEvent.clock_in_at)}</div>
                  </div>
                  <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.25)' }} />
                  <div>
                    <div style={{ fontSize: 10, color: '#bbf7d0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Out</div>
                    <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>{formatTimeShort(clockEvent.clock_out_at)}</div>
                  </div>
                  <div style={{ width: 1, height: 28, background: 'rgba(255,255,255,0.25)' }} />
                  <div>
                    <div style={{ fontSize: 10, color: '#bbf7d0', textTransform: 'uppercase', letterSpacing: 0.5 }}>Total</div>
                    <div style={{ fontSize: 15, fontWeight: 800, color: '#FFD000' }}>
                      {clockEvent.hours_worked ? `${clockEvent.hours_worked.toFixed(2)}h` : '—'}
                    </div>
                  </div>
                </div>
                <div style={{
                  background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.3)',
                  borderRadius: 0, padding: '6px 10px', fontSize: 11, fontWeight: 700, color: '#fff',
                }}>
                  ✓ Done
                </div>
              </div>
            )}
          </div>
        )}

        {/* Fixed bottom tab bar — curved notch + raised active button */}
        <BottomNav tabs={tabs} currentTab={currentTab} onTabChange={onTabChange} />
      </>

      {/* CSS to show/hide per breakpoint */}
      <style>{`
        @media (min-width: 769px) {
          .desktop-header       { display: block !important; }
          .mobile-header        { display: none !important; }
          .mobile-hourly-header { display: none !important; }
          .mobile-comm-header   { display: none !important; }
          .mobile-oc-header     { display: none !important; }
          .acts-bottomnav       { display: none !important; }
          .mobile-clock-bar     { display: none !important; }
        }
        @media (max-width: 768px) {
          .desktop-header       { display: none !important; }
          .mobile-header        { display: block !important; }
          .mobile-hourly-header { display: block !important; }
          .mobile-comm-header   { display: block !important; }
          .mobile-oc-header     { display: block !important; }
          .acts-bottomnav       { display: block !important; }
          .mobile-clock-bar     { display: block !important; }
        }
        @media print {
          .desktop-header       { display: none !important; }
          .mobile-header        { display: none !important; }
          .mobile-hourly-header { display: none !important; }
          .mobile-comm-header   { display: none !important; }
          .mobile-oc-header     { display: none !important; }
          .acts-bottomnav       { display: none !important; }
          .mobile-clock-bar     { display: none !important; }
        }
      `}</style>

      {/* ── Clock-in reminder — centred pop-up for workers who forget ── */}
      {showReminder && (
        <div
          className="no-print"
          onClick={() => { if (!lockedOut) setReminderDismissed(true); }}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(15,34,51,0.6)', backdropFilter: 'blur(2px)',
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 18,
          }}
        >
          <div
            onClick={e => e.stopPropagation()}
            style={{
              background: '#fff', borderRadius: 18, maxWidth: 380, width: '100%',
              padding: '30px 24px 24px', textAlign: 'center',
              boxShadow: '0 24px 60px rgba(0,0,0,0.35)', borderTop: '6px solid #DC2626',
            }}
          >
            <div style={{ fontSize: 48, lineHeight: 1, marginBottom: 10 }}>⏰</div>
            <h2 style={{ margin: '0 0 6px', fontSize: 21, fontWeight: 800, color: '#0F2233' }}>
              Don't forget to Clock In!
            </h2>
            <p style={{ margin: '0 0 22px', fontSize: 14.5, color: '#6b7280', lineHeight: 1.5 }}>
              Hi {String(user.name || '').split(' ')[0]} — you haven't clocked in yet today.
              {lockedOut ? ' The app stays locked until you do.' : ' Tap below to clock in now.'}
            </p>
            <button
              onClick={handleClockIn}
              disabled={clockLoading}
              style={{
                width: '100%', padding: '16px', border: 'none', borderRadius: 12,
                background: clockLoading ? '#9ca3af' : '#16a34a', color: '#fff',
                fontSize: 17, fontWeight: 800, cursor: clockLoading ? 'wait' : 'pointer',
                letterSpacing: 0.3,
              }}
            >
              {clockLoading ? '📍 Getting GPS…' : '▶ Clock In Now'}
            </button>
            {lockedOut ? (
              <>
                <button
                  onClick={() => onTabChange && onTabChange('Invoice')}
                  style={{
                    marginTop: 14, background: 'none', border: 'none', color: '#9ca3af',
                    fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline',
                  }}
                >
                  I only need my invoice
                </button>
                <p style={{ margin: '14px 0 0', fontSize: 12, color: '#b0b7bf', lineHeight: 1.5 }}>
                  Can't clock in? Call Michel — he can start your day from his side.
                </p>
              </>
            ) : (
              <button
                onClick={() => setReminderDismissed(true)}
                style={{
                  marginTop: 14, background: 'none', border: 'none', color: '#9ca3af',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer', textDecoration: 'underline',
                }}
              >
                Later
              </button>
            )}
          </div>
        </div>
      )}
    </>
  );
}
